import React, { useMemo, useState } from 'react';

const ApiDocs = () => {
  const [copiedId, setCopiedId] = useState(null);

  const baseUrl = useMemo(() => {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return 'https://my-skin.ai';
  }, []);

  const endpointV2 = `${baseUrl}/api/v2/skin-analysis`;
  const endpointV1 = `${baseUrl}/api/v1/skin-analysis`;

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      // Ignore clipboard errors
    }
  };

  const CodeBlock = ({ code, id, title }) => (
    <div className="rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-gray-900 text-gray-100 flex items-center justify-between">
        <span className="text-xs font-medium tracking-wide uppercase">{title}</span>
        <button
          onClick={() => copyToClipboard(code, id)}
          className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600"
        >
          {copiedId === id ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="bg-gray-950 text-gray-100 p-4 overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-white">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gray-900 text-white font-bold flex items-center justify-center">M</div>
            <div>
              <p className="text-sm font-semibold text-gray-900">MySkinAI API</p>
              <p className="text-xs text-gray-500">Integration Documentation</p>
            </div>
          </a>
          <div className="flex items-center space-x-3">
            <a href="/playground" className="text-sm font-medium text-gray-700 hover:text-gray-900">Playground</a>
            <a href="/developers" className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">Developer Portal</a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-8">
        <section className="rounded-3xl border border-gray-200 bg-white p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <p className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 mb-4">
                Public API v2
              </p>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                Full Analysis Contract For Product Integrations
              </h1>
              <p className="text-gray-600 mt-4 max-w-2xl">
                v2 returns structured analysis and optional annotated image data in one request.
                Access remains approval-gated through the developer portal.
              </p>
            </div>
            <div className="rounded-2xl bg-gray-900 text-white p-5 space-y-3">
              <div>
                <p className="text-xs text-gray-300 uppercase tracking-wide mb-1">Primary endpoint</p>
                <code className="text-sm break-all">{endpointV2}</code>
              </div>
              <div>
                <p className="text-xs text-gray-300 uppercase tracking-wide mb-1">Method</p>
                <p className="text-sm">POST</p>
              </div>
              <div>
                <p className="text-xs text-gray-300 uppercase tracking-wide mb-1">Auth header</p>
                <p className="text-sm">MySkinAI-API-Key</p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <article className="rounded-2xl border border-gray-200 bg-white p-5">
            <h2 className="font-semibold text-gray-900 mb-2">1. Request Access</h2>
            <p className="text-sm text-gray-600">Create a developer account and submit a key request.</p>
          </article>
          <article className="rounded-2xl border border-gray-200 bg-white p-5">
            <h2 className="font-semibold text-gray-900 mb-2">2. Approval + Limits</h2>
            <p className="text-sm text-gray-600">Admin approves and sets request limits per key.</p>
          </article>
          <article className="rounded-2xl border border-gray-200 bg-white p-5">
            <h2 className="font-semibold text-gray-900 mb-2">3. Integrate</h2>
            <p className="text-sm text-gray-600">Use v2 contract examples below to wire your app.</p>
          </article>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <article className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Authentication</h2>
            <p className="text-gray-600 text-sm">
              Include your key in the <code className="bg-gray-100 px-1.5 py-0.5 rounded">MySkinAI-API-Key</code> header.
            </p>
            <CodeBlock
              id="auth-header"
              title="Header"
              code={`MySkinAI-API-Key: msk_your_api_key_here`}
            />
          </article>

          <article className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Request Schema (v2)</h2>
            <div className="text-sm text-gray-600 space-y-1">
              <p><code>imageData</code>: string, required (base64 data URI or raw base64)</p>
              <p><code>age</code>: number, optional</p>
              <p><code>primaryConcern</code>: string, optional</p>
              <p><code>includeAnnotatedImage</code>: boolean, optional (default <code>true</code>)</p>
            </div>
            <CodeBlock
              id="request-json"
              title="JSON Body"
              code={`{
  "imageData": "data:image/jpeg;base64,/9j/4AAQSk...",
  "age": 32,
  "primaryConcern": "acne",
  "includeAnnotatedImage": true
}`}
            />
          </article>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Response Contract (v2)</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm text-gray-700">
            <div className="rounded-xl border border-gray-200 p-4 bg-gray-50">
              <p className="font-semibold text-gray-900 mb-2">Top-level fields</p>
              <p><code>success</code> boolean</p>
              <p><code>data</code> structured skin analysis object</p>
              <p><code>annotatedImage</code> Data URI (or <code>null</code> when disabled)</p>
              <p><code>meta</code> {`{ apiVersion, requestId, timestamp, processingTimeMs }`}</p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 bg-gray-50">
              <p className="font-semibold text-gray-900 mb-2">Annotated image behavior</p>
              <p>If <code>includeAnnotatedImage=true</code>, response includes an image Data URI.</p>
              <p>If image rendering cannot complete, the response returns the original input image.</p>
              <p>If <code>includeAnnotatedImage=false</code>, <code>annotatedImage</code> is <code>null</code>.</p>
            </div>
          </div>
          <CodeBlock
            id="success-response"
            title="200 Response"
            code={`{
  "success": true,
  "data": {
    "primaryConcern": { "condition": "Acne", "percentage": 41, "severity": "Moderate" },
    "skinType": "combination",
    "detailedAnalysis": { "acne": { "percentage": 41, "severity": "Moderate", "description": "..." } }
  },
  "annotatedImage": "data:image/png;base64,iVBORw0KGgoAAA...",
  "meta": {
    "apiVersion": "v2",
    "requestId": "7e2d0b22-bf65-4fe8-8e58-7f9d1d0f7c65",
    "timestamp": "2026-03-13T16:42:15.123Z",
    "processingTimeMs": 1943
  }
}`}
          />
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <article className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">cURL</h2>
            <CodeBlock
              id="curl-example"
              title="cURL"
              code={`curl -X POST ${endpointV2} \\
  -H "Content-Type: application/json" \\
  -H "MySkinAI-API-Key: msk_your_api_key_here" \\
  -d '{
    "imageData": "data:image/jpeg;base64,/9j/4AAQSk...",
    "age": 35,
    "primaryConcern": "pigmentation",
    "includeAnnotatedImage": true
  }'`}
            />
          </article>

          <article className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">JavaScript</h2>
            <CodeBlock
              id="js-example"
              title="JavaScript"
              code={`const response = await fetch("${endpointV2}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "MySkinAI-API-Key": "msk_your_api_key_here"
  },
  body: JSON.stringify({
    imageData,
    age: 30,
    primaryConcern: "acne",
    includeAnnotatedImage: true
  })
});

const result = await response.json();
if (!response.ok) throw new Error(result?.error?.message || "Request failed");`}
            />
          </article>

          <article className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Python</h2>
            <CodeBlock
              id="python-example"
              title="Python"
              code={`import requests

payload = {
  "imageData": image_data,
  "age": 30,
  "primaryConcern": "acne",
  "includeAnnotatedImage": True
}

res = requests.post(
  "${endpointV2}",
  headers={
    "Content-Type": "application/json",
    "MySkinAI-API-Key": "msk_your_api_key_here"
  },
  json=payload,
  timeout=120
)

result = res.json()
res.raise_for_status()`}
            />
          </article>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <article className="rounded-2xl border border-gray-200 bg-white p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Error Codes</h2>
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-lg bg-red-50 border border-red-100 flex items-center justify-between">
                <span className="font-medium text-red-800">AUTH_INVALID_KEY</span>
                <span className="text-red-700">401 invalid/missing key</span>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-between">
                <span className="font-medium text-amber-800">RATE_LIMITED</span>
                <span className="text-amber-700">429 request limit reached</span>
              </div>
              <div className="p-3 rounded-lg bg-orange-50 border border-orange-100 flex items-center justify-between">
                <span className="font-medium text-orange-800">VALIDATION_ERROR</span>
                <span className="text-orange-700">400 malformed input</span>
              </div>
              <div className="p-3 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-between">
                <span className="font-medium text-gray-800">PROCESSING_FAILED</span>
                <span className="text-gray-700">500/502 processing issue</span>
              </div>
            </div>
          </article>

          <article className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Versioning</h2>
            <div className="text-sm text-gray-600 space-y-2">
              <p><code>v2</code> is the recommended integration target for full contract + annotated image.</p>
              <p><code>v1</code> remains available for compatibility.</p>
            </div>
            <CodeBlock
              id="version-paths"
              title="Endpoint Paths"
              code={`v2 (recommended): ${endpointV2}
v1 (compatibility): ${endpointV1}`}
            />
          </article>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Next Steps</h2>
          <p className="text-sm text-gray-600 mb-4">
            Use the playground to validate payloads with your key, then move these examples into your backend integration flow.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="/developers" className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">Open Developer Portal</a>
            <a href="/playground" className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">Open Playground</a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ApiDocs;
